import {AxiosInstance} from "axios";
import {Appointment} from "@/core/interfaces/Appointment";

interface CustomerAppointmentRequest {
    limit?: number;
    beneficiary_id?: number
    active?: boolean;
}

/**
 * The counter's choice to text the patient about a creation, a cancellation
 * or a move.
 * No text leaves unless `notify_patient` is true, while the application is
 * told either way. `phone`, a French mobile, replaces the number of the
 * patient file.
 */
export interface PatientNotificationRequest {
    notify_patient?: boolean;
    phone?: string | null;
}

/**
 * What became of the text the counter asked for. Null when none was asked
 * for, or when nothing actually changed (same slot, already canceled).
 *
 * `no_phone` only means the SMS did not leave: an application user still got
 * the push. `no_consent` is the patient's own choice, not a failure.
 */
export type PatientNotificationStatus = 'sent' | 'no_phone' | 'no_consent';

export type PatientNotificationEvent = 'created' | 'canceled' | 'rescheduled';

/** The text the patient would receive, composed server-side so it cannot drift. */
export interface PatientNotificationPreview {
    content: string;
    /** The mobile of the patient file, null when none is known. */
    phone: string | null;
}

export interface AppointmentChangeResult {
    appointment: Appointment;
    patientNotification: PatientNotificationStatus | null;
}

export interface AppointmentRescheduleRequest extends PatientNotificationRequest {
    start_at: string;
    duration?: number;
}

export interface AppointmentCreateRequest {
    /** Absent or null for an appointment that involves no patient (training, laboratory visit). */
    customer_id?: number | null;
    appointmentable_id?: number;
    appointmentable_type?: string;
    appointment_type_id?: number | null;
    beneficiary_id?: number | null;
    /**
     * Access point of the operator — their identity, so a member without an
     * Apodis user account can hold the appointment. The server
     * keeps the legacy `operator_id` in sync from it.
     */
    operator_access_point_id?: number | null;
    start_at: string;
    duration?: number;
    /** Left empty, the server names the appointment after its type. */
    title?: string | null;
    description?: string | null;
    location?: string | null;
    pharmacist_memo?: string | null;
}

/** What opens a health journey alongside a batch of appointments. */
export interface HealthJourneyBatchRequest {
    /** The assistant's summary, kept with the journey and read in the recap. */
    summary?: string | null;
    /** Whether the patient is texted the recap of the series. */
    notify_recap: boolean;
}

/**
 * Why the recap did not leave, when it did not.
 *
 * `no_consent` is a refusal the patient expressed, not a failure: it is said
 * plainly rather than blamed on the pharmacy.
 */
export type HealthJourneyRecapStatus = 'sent' | 'no_phone' | 'no_credit' | 'no_link' | 'no_consent';

export interface HealthJourneyBatchResult {
    appointments: Appointment[];
    healthJourney: {id: number; recap: HealthJourneyRecapStatus} | null;
}

export interface AppointmentEditRequest extends PatientNotificationRequest {
    /** Absent keeps the patient, null detaches it. */
    customer_id?: number | null;
    appointmentable_id?: number;
    appointmentable_type?: string;
    appointment_type_id?: number | null;
    beneficiary_id?: number | null;
    /**
     * Access point of the operator — their identity, so a member without an
     * Apodis user account can hold the appointment. The server
     * keeps the legacy `operator_id` in sync from it.
     */
    operator_access_point_id?: number | null;
    start_at: string;
    duration?: number;
    title?: string | null;
    description?: string | null;
    location?: string | null;
    pharmacist_memo?: string | null;
}

const changeResult = (body: {data: Appointment; meta?: {patient_notification?: PatientNotificationStatus | null}}) => {
    return {
        appointment: body.data,
        patientNotification: body.meta?.patient_notification ?? null
    };
};

const AppointmentResource = (hdsClient: AxiosInstance) => {
    return {
        /**
         * Get the customer appointments.
         */
        async customerAppointments(customerId: number | string, request: CustomerAppointmentRequest = {}): Promise<Appointment[]> {
            const response = await hdsClient.get(`customers/${customerId}/appointments`, {
                params: request
            });

            return response.data.data;
        },


        /**
         * Export the customer appointments as a PDF file.
         */
        async exportCustomerAppointments(customerId: number | string, request: CustomerAppointmentRequest = {}): Promise<Blob> {
            const response = await hdsClient.get(`customers/${customerId}/appointments/export`, {
                params: request,
                responseType: 'blob'
            });

            return response.data;
        },

        /**
         * Create an appointment from the counter.
         *
         * This and the two methods below are the single appointment creation
         * and edition surface. The creation URL is shared with the patient
         * application, which books
         * event slots through it: the server tells the two apart by their
         * authentication guard.
         */
        async createAppointment(
            organizationId: number,
            request: AppointmentCreateRequest & PatientNotificationRequest
        ): Promise<AppointmentChangeResult> {
            const response = await hdsClient.post(`organizations/${organizationId}/appointments`, {data: request});

            return changeResult(response.data);
        },

        /**
         * Create several appointments at once.
         */
        async createAppointmentBatch(organizationId: number, requests: AppointmentCreateRequest[]): Promise<Appointment[]> {
            const response = await hdsClient.post(`organizations/${organizationId}/appointments/batch`, {
                data: {appointments: requests}
            });

            return response.data.data;
        },

        /**
         * Create the appointments of a health journey, as one series.
         *
         * The same endpoint as the batch above, called apart because it reads
         * the envelope: the journey is created with the appointments, and the
         * recap SMS is attempted right after, so its outcome comes back in
         * `meta` and has to reach the pharmacist — a patient who was never
         * texted is something the counter has to know before they leave.
         */
        async createHealthJourneyBatch(
            organizationId: number,
            requests: AppointmentCreateRequest[],
            journey: HealthJourneyBatchRequest
        ): Promise<HealthJourneyBatchResult> {
            const response = await hdsClient.post(`organizations/${organizationId}/appointments/batch`, {
                data: {appointments: requests, health_journey: journey}
            });

            return {
                appointments: response.data.data,
                healthJourney: response.data.meta?.health_journey ?? null
            };
        },

        /**
         * Edit an appointment from the counter. A new time is texted to the
         * patient only when asked for.
         */
        async editAppointment(
            organizationId: number,
            appointmentId: number,
            request: AppointmentEditRequest
        ): Promise<AppointmentChangeResult> {
            const response = await hdsClient.patch(
                `organizations/${organizationId}/appointments/${appointmentId}`,
                {data: request}
            );

            return changeResult(response.data);
        },

        /**
         * Cancel a given appointment, texting the patient only when asked for.
         */
        async cancelAppointment(
            appointmentId: number | string,
            request: PatientNotificationRequest = {}
        ): Promise<AppointmentChangeResult> {
            const response = await hdsClient.patch(`appointments/${appointmentId}/cancel`, {data: request});

            return changeResult(response.data);
        },

        /**
         * The text the patient would receive about a cancellation or a move to
         * `startAt`, with the mobile of their file.
         */
        async previewPatientNotification(
            appointmentId: number,
            event: Exclude<PatientNotificationEvent, 'created'>,
            startAt?: string
        ): Promise<PatientNotificationPreview> {
            const response = await hdsClient.get(`appointments/${appointmentId}/patient-notification-preview`, {
                params: {event, start_at: startAt}
            });

            return response.data.data;
        },

        /**
         * The confirmation the patient would receive for an appointment not
         * created yet, starting at `startAt`, with the mobile of their file.
         */
        async previewCreationNotification(
            organizationId: number,
            customerId: number,
            startAt: string
        ): Promise<PatientNotificationPreview> {
            const response = await hdsClient.get(`organizations/${organizationId}/appointments/patient-notification-preview`, {
                params: {event: 'created', customer_id: customerId, start_at: startAt}
            });

            return response.data.data;
        },

        /**
         * Move an appointment to a new schedule. The reminder is rescheduled
         * server-side; the patient is texted only when asked for.
         */
        async rescheduleAppointment(
            appointmentId: number,
            request: AppointmentRescheduleRequest
        ): Promise<AppointmentChangeResult> {
            const response = await hdsClient.patch(`appointments/${appointmentId}/reschedule`, {data: request});

            return changeResult(response.data);
        }
    };
};

export default AppointmentResource;
